import type { ListFilters } from '@/components/reports/lists/types';

const ANALYTICS_SUMMARY_URL = 'https://api.pricingsaas.com/functions/v1/api/analytics/summary';
const ANALYTICS_API_KEY = import.meta.env.VITE_ANALYTICS_API_KEY;

export interface AnalyticsSummaryRequest {
  period?: string; // "latest" for current data
  periods?: string[]; // Array of quarters like ["2025Q4", "2024Q4"]
  filters?: ListFilters;
}

export interface AnalyticsSummaryResponse {
  period: string | null;
  filters: Record<string, any>;
  summary: {
    total_companies: number;
    total_pages: number;
    total_plans?: number;
    total_addons?: number;
    companies_with_addons: number;
    companies_with_multiple_pages: number;
    pages_with_license_pricing: number;
    pages_with_usage_pricing: number;
    pages_with_credit_pricing: number;
    pages_with_thresholds: number;
  };
  pricing_models: {
    companies_with_public_pricing: number;
    percent_with_public_pricing: number;
    companies_with_freemium: number;
    percent_with_freemium: number;
    companies_with_trial: number;
    percent_with_trial: number;
    companies_with_contact_pricing: number;
    percent_with_contact_pricing: number;
  };
  by_category: Record<string, {
    category_name: string;
    companies: number;
    pages: number;
  }>;
  by_employees: Record<string, {
    companies: number;
    pages: number;
  }>;
  metadata: {
    execution_time_ms: number;
    generated_at: string;
  };
}

export async function fetchAnalyticsSummary(
  request: AnalyticsSummaryRequest
): Promise<AnalyticsSummaryResponse> {
  // Check if API key is configured
  if (!ANALYTICS_API_KEY) {
    throw new Error('Analytics API key not configured. Please add VITE_ANALYTICS_API_KEY to your .env file.');
  }

  console.log('📡 [analyticsSummaryAPI] Fetching with request:', JSON.stringify(request, null, 2));

  try {
    const response = await fetch(ANALYTICS_SUMMARY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${ANALYTICS_API_KEY}`,
      },
      body: JSON.stringify(request)
    });

    console.log('📡 [analyticsSummaryAPI] Response status:', response.status);

    if (!response.ok) {
      // Try to parse error response
      let errorMessage = 'Failed to fetch analytics summary';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        // If parsing fails, use status text
        errorMessage = `${errorMessage}: ${response.statusText}`;
      }

      // Handle specific status codes
      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication failed. Please sign in again.');
      } else if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later.');
      } else if (response.status >= 500) {
        throw new Error('Analytics service is temporarily unavailable. Please try again later.');
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log('📡 [analyticsSummaryAPI] Response data:', JSON.stringify({
      total_companies: data.summary?.total_companies,
      total_pages: data.summary?.total_pages,
      filters: data.filters,
      pages_with_addons: data.summary?.pages_with_addons,
      percent_pages_with_addons: data.summary?.percent_pages_with_addons
    }, null, 2));
    return data as AnalyticsSummaryResponse;
  } catch (error) {
    // Re-throw our custom errors
    if (error instanceof Error) {
      throw error;
    }

    // Handle network errors
    throw new Error('Failed to connect to analytics service. Please check your internet connection.');
  }
}
